import { API_BASE } from '../apiBase';
import BillToPicker from '../lib/BillToPicker';
import { fetchOrgAddressMap, billingAddressFor } from '../lib/orgAddress';
import { fmtPhone, fmtPhoneInput } from '../lib/phone';
// src/billing/CreateQuote.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import QuickCreateBid from './QuickCreateBid';

function CreateQuote() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');   // present when editing an existing quote
  const bidParam = searchParams.get('bid'); // present when starting a quote from a bid

  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

  // Resume the in-progress draft ONLY when arriving fresh. Arriving with ?id=
  // (edit an existing quote) or ?bid= (new quote for a bid) must start clean —
  // otherwise the previous draft's project/items bleed into the wrong quote.
  const savedDraft = (editId || bidParam || searchParams.get('project')) ? {} : (JSON.parse(localStorage.getItem('draftQuote')) || {});

  const [projectSearch, setProjectSearch] = useState(savedDraft.projectSearch || '');
  const [selectedProject, setSelectedProject] = useState(savedDraft.selectedProject || null);
  // WHICH PERSON this quote goes to — null = the project's own customer.
  const [billToCust, setBillToCust] = useState(null);
  const [orgAddrs, setOrgAddrs] = useState(new Map());
  useEffect(() => { fetchOrgAddressMap().then(setOrgAddrs); }, []);
  const [currency, setCurrency] = useState(savedDraft.currency || 'USD');
  const [lineItems, setLineItems] = useState(savedDraft.lineItems || [{ id: generateId(), description: '', quantity: 1, unitPrice: 0 }]);
  const [validUntil, setValidUntil] = useState(savedDraft.validUntil || '30');
  const [memo, setMemo] = useState(savedDraft.memo || '');
  const [showSignature, setShowSignature] = useState(savedDraft.showSignature ?? false);
  const [showPayLink, setShowPayLink] = useState(savedDraft.showPayLink ?? false);
  const [showCardFee, setShowCardFee] = useState(savedDraft.showCardFee ?? false); // +3.5% card total line
  const [showValidFor, setShowValidFor] = useState(savedDraft.showValidFor ?? false);
  const [isLumpSum, setIsLumpSum] = useState(savedDraft.isLumpSum ?? false);
  const [showQtyCol, setShowQtyCol] = useState(savedDraft.showQtyCol ?? true);          // toggle Qty column on the PDF/preview
  const [showUnitPriceCol, setShowUnitPriceCol] = useState(savedDraft.showUnitPriceCol ?? true); // toggle Unit Price column
  // Click any value in the Selected Project card to copy it to the clipboard.
  const [copied, setCopied] = useState('');
  const renderCopy = (id, value) => {
    const text = String(value == null ? '' : value).trim();
    return (
      <span
        onClick={() => { if (text && navigator.clipboard) { navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(''), 1200); } }}
        title={text ? 'Click to copy' : ''}
        style={{ cursor: text ? 'pointer' : 'default', borderBottom: text ? '1px dotted #9bb0c9' : 'none' }}
      >
        {value || 'N/A'}{copied === id ? '  ✓ copied' : ''}
      </span>
    );
  };
  const [showThankYou, setShowThankYou] = useState(savedDraft.showThankYou ?? false);

  // Dedup / edit identity: remember the DB id and number so further saves UPDATE
  // the same quote, and so leaving and returning keeps you on it (number stays
  // locked instead of bumping to the next one / creating a duplicate).
  const [currentQuoteId, setCurrentQuoteId] = useState(savedDraft.currentQuoteId || null);
  const [savedNumber, setSavedNumber] = useState(savedDraft.savedNumber || null);

// NEW: Auto-save to LocalStorage whenever any form field changes
  useEffect(() => {
    const draft = {
      projectSearch, selectedProject, currency, lineItems, validUntil, memo, showSignature, showPayLink, showCardFee, showValidFor, isLumpSum, showQtyCol, showUnitPriceCol,
      currentQuoteId, savedNumber
    };
    localStorage.setItem('draftQuote', JSON.stringify(draft));
  }, [projectSearch, selectedProject, currency, lineItems, validUntil, memo, showSignature, showPayLink, showCardFee, showValidFor, isLumpSum, showQtyCol, showUnitPriceCol, currentQuoteId, savedNumber]);

  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [projects, setProjects] = useState([]);
  const [bids, setBids] = useState([]); // open opportunities — quotes attach here pre-win
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // A bid dressed up as a quote target: same fields buildPDF/save read from a
  // project, plus __bid so numbering & payloads know no PO/project exists yet.
  const targetFromBid = (b) => ({
    __bid: true, bidId: b._id, _id: null, poNumber: null,
    title: b.title,
    // Contact-only bid (no customer linked yet): dress the typed contact up as
    // the customer so the PDF and form have a name/phone/email to bill to.
    customer: b.customer || {
      _id: null, name: b.contactName || '', firstName: '', lastName: '', businessName: '',
      primaryPhone: b.contactPhone || '', primaryEmail: b.contactEmail || '', type: 'Homeowner',
    },
    jobSiteAddress: b.jobSiteAddress || {}, billTo: 'Business',
  });

  // Inline "quick create bid" — creates customer (if new) + bid, NO project/PO
  const [quickProjOpen, setQuickProjOpen] = useState(false);
  const [quickProjSeed, setQuickProjSeed] = useState('');
  const openQuickProject = () => {
    setQuickProjSeed(projectSearch.trim());
    setProjectSearch('');
    setQuickProjOpen(true);
  };


  // For natural typing in unit price field
  const [focusedPriceId, setFocusedPriceId] = useState(null);
  const [rawPriceInput, setRawPriceInput] = useState('');


  const currentYearShort = new Date().getFullYear().toString().slice(-2);

    // Ref for focusing Description inputs
  const descriptionRefs = useRef([]);

  // Drag-to-reorder line items — a custom POINTER drag (not native HTML5 DnD), so a
  // real floating copy of the row follows the cursor, a dashed bar marks the drop
  // gap, and the row lands in place on release with no browser "snap-back".
  const [drag, setDrag] = useState(null);           // { id, fromIndex, w, h, dx, dy } while dragging
  const [pointer, setPointer] = useState(null);     // { x, y } current cursor position
  const [dropIndex, setDropIndex] = useState(null); // insertion gap 0..length (where it'll land)
  const rowRefs = useRef([]);
  const dragRef = useRef(null);                     // mirror for the window pointerup handler

  const moveItem = (from, insertIndex) => {
    if (from == null || insertIndex == null) return;
    if (insertIndex === from || insertIndex === from + 1) return; // dropped into its own gap → no-op
    const arr = [...lineItems];
    const [moved] = arr.splice(from, 1);
    const dest = from < insertIndex ? insertIndex - 1 : insertIndex; // removal above shifts the gap down one
    arr.splice(dest, 0, moved);
    setLineItems(arr);
  };

  // Which gap (0..length) the cursor is over, ignoring the row being dragged.
  const computeDropIndex = (clientY, fromIndex) => {
    for (let i = 0; i < lineItems.length; i++) {
      if (i === fromIndex) continue;
      const el = rowRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return i;
    }
    return lineItems.length;
  };

  const startRowDrag = (e, index) => {
    e.preventDefault();
    const el = rowRefs.current[index];
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = { fromIndex: index, dropIndex: index };
    setDrag({ id: lineItems[index].id, fromIndex: index, w: r.width, h: r.height, dx: e.clientX - r.left, dy: e.clientY - r.top });
    setPointer({ x: e.clientX, y: e.clientY });
    setDropIndex(index);
    const onMove = (ev) => {
      setPointer({ x: ev.clientX, y: ev.clientY });
      const idx = computeDropIndex(ev.clientY, index);
      dragRef.current.dropIndex = idx;
      setDropIndex(idx);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const d = dragRef.current;
      if (d) moveItem(d.fromIndex, d.dropIndex);
      dragRef.current = null;
      setDrag(null); setPointer(null); setDropIndex(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // A dashed indigo bar that opens at the gap where the row will drop.
  const DropBar = () => (
    <div style={{ height: '44px', marginBottom: '12px', borderRadius: '6px', border: '2px dashed #818cf8', background: 'rgba(99,102,241,0.15)', pointerEvents: 'none' }} />
  );

  // NEW: Store quotes for the currently selected project only
  const [projectQuotes, setProjectQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  // ==================== DYNAMIC QUOTE NUMBER ====================
  const getNextQuoteNumber = (poNumber) => {
    if (!poNumber) return `QT${currentYearShort}-XXXX-001`;

    const counters = projectQuotes
      .map(q => {
        const match = q.number.match(/-(\d{3})$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(n => !isNaN(n));

    const maxCounter = counters.length > 0 ? Math.max(...counters) : 0;
    const nextCounter = String(maxCounter + 1).padStart(3, '0');

    return `QT${currentYearShort}-${poNumber}-${nextCounter}`;
  };

  // All quotes (for the date-based series) — bids use QT{yymmdd}-{seq}
  const [allQuotes, setAllQuotes] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/quotes`, { headers: { Authorization: token } })
      .then(r => setAllQuotes(r.data || []))
      .catch(() => {});
  }, []);
  const todayPrefix = useMemo(() => {
    const d = new Date();
    return `QT${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  // Manual override — for recreating a quote (e.g. one that only exists in the
  // dev database) under its original number. Blank = automatic numbering.
  const [numberOverride, setNumberOverride] = useState('');
  // Super-admin in-place edit of the Selected card: the display rows become
  // inputs; Apply patches the in-memory target (this document only).
  const [cardForm, setCardForm] = useState(null);
  const openCardEdit = () => setCardForm({
    number: quoteNumber,
    poNumber: selectedProject.poNumber || '',
    title: selectedProject.title || '',
    name: selectedProject.customer?.name || '',
    businessName: selectedProject.customer?.businessName || '',
    phone: selectedProject.customer?.primaryPhone || '',
    email: selectedProject.customer?.primaryEmail || '',
    street: selectedProject.jobSiteAddress?.street || '',
    city: selectedProject.jobSiteAddress?.city || '',
    state: selectedProject.jobSiteAddress?.state || 'CA',
    zip: selectedProject.jobSiteAddress?.zip || '',
  });
  const applyCardEdit = () => {
    if (cardForm.number.trim() && cardForm.number.trim() !== quoteNumber) setNumberOverride(cardForm.number.trim());
    setSelectedProject({
      ...selectedProject,
      poNumber: selectedProject.__bid ? selectedProject.poNumber : (cardForm.poNumber.trim() || selectedProject.poNumber),
      title: cardForm.title,
      customer: { ...(selectedProject.customer || {}), name: cardForm.name, businessName: cardForm.businessName, primaryPhone: cardForm.phone, primaryEmail: cardForm.email },
      jobSiteAddress: { ...(selectedProject.jobSiteAddress || {}), street: cardForm.street, city: cardForm.city, state: cardForm.state, zip: cardForm.zip },
    });
    setCardForm(null);
  };
  const ci = { padding: '3px 8px', border: '1px solid #ccd0d5', borderRadius: '5px', fontSize: '13px', margin: '2px 0 2px 6px' };

  // Replace the old quoteNumber definition with this useMemo
const quoteNumber = useMemo(() => {
  if (numberOverride.trim()) return numberOverride.trim(); // manual override wins
  if (savedNumber) return savedNumber; // editing/saved: keep the committed number

  // Quoting a BID → date-based series, independent of any PO:
  // QT260706-01, -02… (count of today's quotes + 1)
  if (selectedProject?.__bid) {
    const todays = allQuotes.filter(q => (q.number || '').startsWith(todayPrefix));
    const seqs = todays.map(q => { const m = q.number.match(/-(\d{2,})$/); return m ? parseInt(m[1], 10) : 0; });
    const next = (seqs.length ? Math.max(...seqs) : 0) + 1;
    return `${todayPrefix}-${String(next).padStart(2, '0')}`;
  }

  if (!selectedProject?.poNumber) return 'QT--XXXX-001 (select a bid or project)';

  const poNumber = selectedProject.poNumber;
  const counters = projectQuotes
    .map(q => {
      const match = q.number.match(/-(\d{3})$/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(n => !isNaN(n));

  const maxCounter = counters.length > 0 ? Math.max(...counters) : 0;
  const nextCounter = String(maxCounter + 1).padStart(3, '0');

  return `QT${currentYearShort}-${poNumber}-${nextCounter}`;
}, [selectedProject, projectQuotes, currentYearShort, savedNumber, allQuotes, todayPrefix, numberOverride]);

  // Live preview: render the REAL jsPDF to an iframe (WYSIWYG), always on —
  // even with no project selected, so a default mockup quote shows by default.
  // buildPDF() is fully null-safe. Mirrors CreateInvoice.
  const [livePdfUrl, setLivePdfUrl] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const url = buildPDF().output('bloburl');
        setLivePdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      } catch (e) {
        // ignore transient render errors mid-typing
      }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, billToCust, lineItems, currency, memo, showSignature, showPayLink, showCardFee, showValidFor, isLumpSum, showQtyCol, showUnitPriceCol, showThankYou, validUntil, quoteNumber]);
  // =================================================================

  useEffect(() => {
    const fetchProjects = async () => {
      const token = localStorage.getItem('token');
      if (!token) return navigate('/login');
      try {
        setLoadingProjects(true);
        setFetchError(null);
        const res = await axios.get(`${API_BASE}/projects`, {
          headers: { Authorization: token },
        });
        setProjects(res.data);
        // open bids (not yet promoted) — the usual quote target
        try {
          const bres = await axios.get(`${API_BASE}/bids`, { headers: { Authorization: token } });
          setBids((bres.data || []).filter(b => !b.project));
        } catch (e) { /* bids API not available — projects still work */ }
      } catch (err) {
        console.error('Fetch projects error:', err);
        setFetchError('Failed to load projects: ' + (err.response?.data?.error || err.message));
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [navigate]);

  // Arriving with ?bid= (from a bid drawer's "New quote for this bid"):
  // preselect that bid as the quote target.
  useEffect(() => {
    const bidParam = searchParams.get('bid');
    if (!bidParam || editId) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/bids/${bidParam}`, { headers: { Authorization: token } })
      .then(({ data: b }) => setSelectedProject(targetFromBid(b)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arriving from a project's Revenue tab (?project=) — preselect that project.
  useEffect(() => {
    const projParam = searchParams.get('project');
    if (!projParam || editId || projects.length === 0 || selectedProject) return;
    const proj = projects.find((p) => p._id === projParam);
    if (proj) setSelectedProject(proj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // EDIT MODE: when arriving with ?id=, load that quote into the form. Setting
  // currentQuoteId + savedNumber makes the next save UPDATE this record (no
  // duplicate) and keeps its original number.
  useEffect(() => {
    if (!editId) return;
    const loadQuote = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data: q } = await axios.get(`${API_BASE}/quotes/${editId}`, { headers: { Authorization: token } });
        const projCustRaw = q.project ? q.project.customer : null;
        const samePerson = !projCustRaw || !q.customer || String(projCustRaw?._id || projCustRaw) === String(q.customer._id);
        const proj = q.project
          ? { ...q.project, customer: samePerson ? (q.customer || projCustRaw) : (typeof projCustRaw === 'object' ? projCustRaw : { _id: projCustRaw }) }
          : null;
        if (proj) setSelectedProject(proj);
        if (!samePerson) setBillToCust(q.customer);
        else if (q.bid) {
          let linked = false;
          try {
            const { data: b } = await axios.get(`${API_BASE}/bids/${q.bid}`, { headers: { Authorization: token } });
            setSelectedProject(targetFromBid(b));
            linked = true;
          } catch (e) { /* bid gone — fall through to customer-only below */ }
          if (!linked && q.customer) setSelectedProject({ __bid: true, bidId: q.bid, _id: null, poNumber: null, title: '', customer: q.customer, jobSiteAddress: {}, billTo: 'Business' });
        } else if (q.customer) {
          // Older quote with no bid/project link — still show the customer so
          // "QUOTE TO:" renders correctly.
          setSelectedProject({ __bid: true, bidId: null, _id: null, poNumber: null, title: '', customer: q.customer, jobSiteAddress: {}, billTo: 'Business' });
        }
        setCurrentQuoteId(q._id);
        setSavedNumber(q.number);
        setSavedStatus(q.status || 'Draft');
        setCurrency(q.currency || 'USD');
        if (Array.isArray(q.lineItems) && q.lineItems.length) {
          setLineItems(q.lineItems.map((i) => ({
            id: generateId(),
            description: i.description || '',
            quantity: i.quantity ?? 1,
            unitPrice: i.unitPrice ?? 0,
          })));
        }
        setMemo(q.memo || '');
        if (typeof q.isLumpSum === 'boolean') setIsLumpSum(q.isLumpSum);
        if (typeof q.showSignature === 'boolean') setShowSignature(q.showSignature);
        if (typeof q.showPayLink === 'boolean') setShowPayLink(q.showPayLink);
        if (typeof q.showCardFee === 'boolean') setShowCardFee(q.showCardFee);
        if (typeof q.showValidFor === 'boolean') setShowValidFor(q.showValidFor);
        if (typeof q.showQtyCol === 'boolean') setShowQtyCol(q.showQtyCol);
        if (typeof q.showUnitPriceCol === 'boolean') setShowUnitPriceCol(q.showUnitPriceCol);
        if (q.validUntil && q.createdAt) {
          const days = Math.max(1, Math.round((new Date(q.validUntil) - new Date(q.createdAt)) / 86400000));
          setValidUntil(String(days));
        }
      } catch (err) {
        alert('Failed to load quote for editing: ' + (err.response?.data?.error || err.message));
      }
    };
    loadQuote();
  }, [editId]);

  // NEW: When a project is selected, fetch only its quotes
  useEffect(() => {
    if (!selectedProject?._id) {
      setProjectQuotes([]);
      return;
    }

    const fetchProjectQuotes = async () => {
      setLoadingQuotes(true);
      const token = localStorage.getItem('token');
      try {
        const res = await axios.get(
          `${API_BASE}/quotes?project=${selectedProject._id}`,
          { headers: { Authorization: token } }
        );
        setProjectQuotes(res.data || []);
      } catch (err) {
        console.error('Failed to fetch project quotes:', err);
        setProjectQuotes([]);
      } finally {
        setLoadingQuotes(false);
      }
    };

    fetchProjectQuotes();
  }, [selectedProject]);

  // Informal Estimate Log Notes for the selected project — powers the
  // "+ Add item from Informal Estimates" picker. Non-fatal if absent.
  const [projectInformalEstimates, setProjectInformalEstimates] = useState([]);
  const [showIePicker, setShowIePicker] = useState(false);
  // Which line item came from which estimate. An estimate is grayed out in
  // the picker only while its line item is STILL on the document — remove or
  // clear the line and the estimate un-grays automatically.
  const [ieLinks, setIeLinks] = useState({}); // lineItemId -> estimateId
  const addedIeIds = lineItems.map(li => ieLinks[li.id]).filter(Boolean);
  useEffect(() => {
    setShowIePicker(false);
    setIeLinks({});
    if (!selectedProject?._id) { setProjectInformalEstimates([]); return; }
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/projects/${selectedProject._id}/informal-estimates`, { headers: { Authorization: token } })
      .then(r => setProjectInformalEstimates(r.data || []))
      .catch(() => setProjectInformalEstimates([]));
  }, [selectedProject]);
  const addItemFromInformalEstimate = (ie) => {
    if (addedIeIds.includes(ie._id)) return; // already on the document
    const item = { id: generateId(), description: ie.description || '', quantity: 1, unitPrice: Number(ie.amount) || 0 };
    setLineItems(prev => {
      // Replace a single still-blank row instead of stacking under it.
      const blank = prev.length === 1 && !prev[0].description.trim() && !Number(prev[0].unitPrice);
      return blank ? [item] : [...prev, item];
    });
    setIeLinks(prev => ({ ...prev, [item.id]: ie._id }));
    setShowIePicker(false);
  };

const filteredProjects = useMemo(() => {
    if (!projectSearch) return [];
    const searchLower = projectSearch.toLowerCase();
    
    return projects.filter(
      (p) =>
        p.poNumber?.toLowerCase().includes(searchLower) ||
        p.title?.toLowerCase().includes(searchLower) ||
        p.customer?.name?.toLowerCase().includes(searchLower)
    );
  }, [projects, projectSearch]);

  const filteredBids = useMemo(() => {
    if (!projectSearch) return [];
    const s = projectSearch.toLowerCase();
    return bids.filter(
      (b) =>
        b.title?.toLowerCase().includes(s) ||
        b.customer?.name?.toLowerCase().includes(s) ||
        b.jobSiteAddress?.street?.toLowerCase().includes(s)
    );
  }, [bids, projectSearch]);

  const addLineItem = () => setLineItems([...lineItems, { id: generateId(), description: '', quantity: 1, unitPrice: 0 }]);

  const updateItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const removeItem = (index) => {
    // Last remaining row: X clears it instead of removing (there's always one row).
    if (lineItems.length > 1) setLineItems(lineItems.filter((_, i) => i !== index));
    else setLineItems([{ id: generateId(), description: '', quantity: 1, unitPrice: 0 }]);
  };

  const subtotal = lineItems.reduce((sum, i) => sum + (Number(i.quantity) * Number(i.unitPrice)), 0);

  const handleDescriptionKeyDown = (e, index) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (lineItems[index].description.trim() !== '') {
        addLineItem();
        setTimeout(() => {
          if (descriptionRefs.current[index + 1]) {
            descriptionRefs.current[index + 1].focus();
          }
        }, 0);
      }
    }
  };

  const formatPrice = (value) => {
    if (value === '' || value == null || isNaN(value)) return '0.00';
    return Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const displayUnitPrice = (id, value) => {
    if (focusedPriceId === id) {
      return rawPriceInput;
    }
    return formatPrice(value);
  };

const handleClear = () => {
    if (window.confirm('Are you sure you want to clear this quote? All unsaved changes will be lost.')) {
      localStorage.removeItem('draftQuote'); // Wipe the cache memory

      setCurrentQuoteId(null);   // start a fresh quote identity
      setSavedNumber(null);      // let the number recompute (next variant)
      setSelectedProject(null);
      setProjectSearch('');
      setLineItems([{ id: generateId(), description: '', quantity: 1, unitPrice: 0 }]);
      setMemo('');
setIsLumpSum(false); 
      setShowValidFor(false);
      setShowPayLink(false); 
      setShowSignature(false);
      setShowThankYou(true);
      setValidUntil('30');
    }
  };

  // A sent quote is view-only: no edits, no re-save. "Save as revision" stays
  // available — it never edits the sent paper, it files a new {base}R{n}.
  const [savedStatus, setSavedStatus] = useState('Draft');
  const sentLocked = savedStatus !== 'Draft';

  // ==================== NEW: HANDLE SAVE (only addition) ====================
  const handleSave = async () => {
    if (sentLocked) return alert('This quote has been sent and is view-only. Use "Save as revision", or delete it and create a new one.');
    if (!selectedProject) return alert('Please select a bid or project');
    if (lineItems.every((i) => !i.description.trim())) return alert('Add at least one line item');
    const payload = {
      number: quoteNumber,
      project: selectedProject.__bid ? undefined : selectedProject._id,
      bid: selectedProject.__bid ? selectedProject.bidId : undefined,
      customer: billToCust?._id || selectedProject.customer._id,
      currency,
      lineItems: lineItems
        .filter((i) => i.description.trim())
        .map((item) => ({
          ...item,
          amount: Number(item.quantity) * Number(item.unitPrice),
        })),
      subtotal,
      tax: 0,
      total: subtotal,
      memo,
      showSignature,
      showPayLink,
      showCardFee,
      showValidFor,
      isLumpSum,
      showQtyCol,
      showUnitPriceCol,
      validUntil: new Date(Date.now() + Number(validUntil) * 24 * 60 * 60 * 1000),
      status: currentQuoteId ? undefined : 'Draft', // updates keep the current status
    };
    try {
      const token = localStorage.getItem('token');
      if (currentQuoteId) {
        await axios.put(`${API_BASE}/quotes/${currentQuoteId}`, payload, { headers: { Authorization: token } });
      } else {
        const res = await axios.post(`${API_BASE}/quotes`, payload, { headers: { Authorization: token } });
        setCurrentQuoteId(res.data._id);
        setSavedNumber(res.data.number);
      }
      setToastMessage(currentQuoteId ? 'Quote updated successfully!' : 'Quote saved successfully as Draft!');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (err) {
      alert('Failed to save quote: ' + (err.response?.data?.error || err.message));
    }
  };
  // =====================================================================

  // ---- Save as revision: the current quote becomes 'Revised' (kept for the
  // record), and a NEW quote is created as {base}R{n} with the edits you've
  // made. Two different numbers on two different PDFs — no ambiguity about
  // which paper the customer signed.
  const handleSaveRevision = async () => {
    if (!currentQuoteId || !savedNumber) return;
    if (lineItems.every((i) => !i.description.trim())) return alert('Add at least one line item');
    const base = savedNumber.replace(/R\d+$/, '');
    // next revision index across everything sharing this base number
    const revs = allQuotes
      .filter(q => (q.number || '').replace(/R\d+$/, '') === base)
      .map(q => { const m = (q.number || '').match(/R(\d+)$/); return m ? parseInt(m[1], 10) : 1; });
    const nextRev = Math.max(1, ...revs) + 1;
    const revNumber = `${base}R${nextRev}`;

    const payload = {
      number: revNumber,
      project: selectedProject.__bid ? undefined : selectedProject._id,
      bid: selectedProject.__bid ? selectedProject.bidId : undefined,
      customer: billToCust?._id || selectedProject.customer._id,
      currency,
      lineItems: lineItems.filter((i) => i.description.trim()).map((item) => ({ ...item, amount: Number(item.quantity) * Number(item.unitPrice) })),
      subtotal, tax: 0, total: subtotal,
      memo,
      showSignature, showPayLink, showCardFee, showValidFor, isLumpSum, showQtyCol, showUnitPriceCol,
      validUntil: new Date(Date.now() + Number(validUntil) * 24 * 60 * 60 * 1000),
      status: 'Draft',
    };
    try {
      const token = localStorage.getItem('token');
      // retire the old paper first, then mint the revision
      await axios.put(`${API_BASE}/quotes/${currentQuoteId}`, { status: 'Revised' }, { headers: { Authorization: token } });
      const res = await axios.post(`${API_BASE}/quotes`, payload, { headers: { Authorization: token } });
      setCurrentQuoteId(res.data._id);
      setSavedNumber(res.data.number);
      setAllQuotes(prev => [res.data, ...prev]);
      setToastMessage(`Revision saved — you're now editing ${revNumber}`);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3500);
    } catch (err) {
      alert('Failed to save revision: ' + (err.response?.data?.error || err.message));
    }
  };

  // Create a ready-to-send draft (quote PDF attached, customer pre-filled) in the
  // user's Outlook Drafts — same behavior as CreateInvoice's Send. Reuses the
  // generic /invoices/create-outlook-draft endpoint (it's content-agnostic).
  // Send-button stage: '' | 'saving' | 'pdf' | 'draft' — the button narrates
  // the wait instead of sitting silent for several seconds.
  const [sendStage, setSendStage] = useState('');

  const createOutlookDraft = async () => {
    // Fetch the LATEST customer at send time — the page's copy is a snapshot
    // from page load (or a restored draft) and goes stale if the customer was
    // edited since (name, email, etc.).
    let customer = billToCust || selectedProject?.customer || {};
    const custId = customer._id || (typeof customer === 'string' ? customer : null);
    if (custId) {
      try {
        const { data } = await axios.get(`${API_BASE}/customers/${custId}`, { headers: { Authorization: localStorage.getItem('token') } });
        if (data) customer = data;
      } catch (_) { /* fall back to the snapshot */ }
    }
    const email = customer.primaryEmail || customer.altEmail || '';
    const firstName = customer.firstName || (customer.name || '').trim().split(/\s+/)[0] || 'there';
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
    const subject = `Quote ${quoteNumber} — Stalwart Electric`;
    const body =
      `Good ${timeOfDay} ${firstName},\n\n` +
      `Attached to this email is a copy of Quote # ${quoteNumber}. ` +
      `If you have any comments, questions or concerns please contact me.\n\n` +
      `Thank you,`;
    if (!email) alert('This customer has no email on file — the draft will open with an empty "To" field.');
    try {
      const token = localStorage.getItem('token');
      setSendStage('pdf');
      // let the button repaint before the PDF build blocks the main thread
      await new Promise(r => setTimeout(r, 30));
      const pdfBase64 = buildPDF().output('datauristring');
      const filename = `Quote_${quoteNumber.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      setSendStage('draft');
      await axios.post(
        `${API_BASE}/invoices/create-outlook-draft`,
        { to: email, subject, body, pdfBase64, filename },
        { headers: { Authorization: token } }
      );
      // The quote left the building — reflect that (Draft → Sent) if it's saved.
      if (currentQuoteId) {
        axios.put(`${API_BASE}/quotes/${currentQuoteId}`, { status: 'Sent' }, { headers: { Authorization: token } }).catch(() => {});
        setSavedStatus('Sent'); // locks the form — view-only from here
      }
      setToastMessage('Draft with PDF created in your Outlook Drafts — review & send');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 4000);
    } catch (err) {
      alert('Could not create the Outlook draft: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSaveQuote = async () => {
    if (!selectedProject) return alert('Please select a bid or project');
    if (lineItems.every((i) => !i.description.trim())) return alert('Add at least one line item');
    if (sendStage) return; // already in flight
    setSendStage('saving');
    const payload = {
      number: quoteNumber,
      project: selectedProject.__bid ? undefined : selectedProject._id,
      bid: selectedProject.__bid ? selectedProject.bidId : undefined,
      customer: billToCust?._id || selectedProject.customer._id,
      currency,
      lineItems: lineItems
        .filter((i) => i.description.trim())
        .map((item) => ({
          ...item,
          amount: Number(item.quantity) * Number(item.unitPrice),
        })),
      subtotal,
      tax: 0,
      total: subtotal,
      memo,
      showSignature,
      showPayLink,
      showCardFee,
      showValidFor,
      isLumpSum,
      showQtyCol,
      showUnitPriceCol,
      validUntil: new Date(Date.now() + Number(validUntil) * 24 * 60 * 60 * 1000),
      status: currentQuoteId ? undefined : 'Draft', // updates keep the current status
    };
    try {
      const token = localStorage.getItem('token');
      if (currentQuoteId) {
        await axios.put(`${API_BASE}/quotes/${currentQuoteId}`, payload, { headers: { Authorization: token } });
      } else {
        const res = await axios.post(`${API_BASE}/quotes`, payload, { headers: { Authorization: token } });
        setCurrentQuoteId(res.data._id);
        setSavedNumber(res.data.number);
      }
      // Save first, then drop a ready-to-send draft (quote PDF attached) into Outlook Drafts.
      await createOutlookDraft();
    } catch (err) {
      alert('Failed to create quote: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendStage('');
    }
  };

  const buildPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // 1. Top Section (Logo, Title, and Quote Details)
    
    // ONLY the logo is moved up to the top right (Y=12)
    doc.addImage('/media/logo_round.png', 'PNG', 163, 12, 32, 32, 'logo', 'NONE');

    // Title remains exactly where it was (Y=22)
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('QUOTE', 15, 22); 

    // Details remain exactly where they were (Starting at Y=30)
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    let topY = 30; 
    doc.text(`Quote # ${quoteNumber}`, 15, topY); topY += 5;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 15, topY); topY += 5;
    if (showValidFor) {
      doc.text(`Valid for ${validUntil} days`, 15, topY);
    }

    // 2. Address Headers (Shifted up & aligned)
    const headerY = 50; 
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('STALWART ELECTRIC', 15, headerY);
    doc.text('QUOTE TO:', 75, headerY); // <-- Shifted left to 75
    doc.text('PROJECT ADDRESS:', 140, headerY);

    // 3. Address Content (Tightened line spacing)
    const contentY = headerY + 6; 
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    
    let leftY = contentY;
    doc.text('4712 Solano Way', 15, leftY); leftY += 5;
    doc.text('Fair Oaks, CA 95628', 15, leftY); leftY += 5;
    doc.text('United States', 15, leftY); leftY += 5;
    doc.text('CSLB Lic. #1109509 · C-10', 15, leftY); leftY += 5; // required on all bids (B&P §7030.5)

    let middleY = contentY;
    doc.setFontSize(11);
    // Shifted from 85 to 75
    const qCust = billToCust || selectedProject?.customer || {};
    // Show business name + Attn only when the customer has a business AND this
    // project is set to bill the business (billTo !== 'Individual').
    if (qCust.businessName && selectedProject?.billTo !== 'Individual') {
      doc.text(qCust.businessName, 75, middleY); middleY += 5;
      doc.text(`Attn: ${qCust.name || ''}`.trim(), 75, middleY); middleY += 5;
    } else {
      doc.text(qCust.name || 'Client Name', 75, middleY); middleY += 5;
    }
    const billing = billingAddressFor(billToCust || selectedProject?.customer || {}, orgAddrs);
    let billingLine = '';
    if (billing.street) billingLine += billing.street + ', ';
    if (billing.city) billingLine += billing.city + ', ';
    if (billing.state) billingLine += billing.state + ' ';
    if (billing.zip) billingLine += billing.zip;
    if (billing.country && billing.country !== 'USA') billingLine += ', ' + billing.country;
    
    let phoneStartY = middleY;
    if (billingLine.trim()) {
      const addressLines = doc.splitTextToSize(billingLine.trim(), 50);
      // Shifted from 85 to 75
      doc.text(addressLines, 75, middleY); 
      phoneStartY = middleY + (addressLines.length * 5);
    }
    if (selectedProject?.customer?.primaryPhone) {
      doc.setFontSize(11);
      // Shifted from 85 to 75
      doc.text(fmtPhone(selectedProject.customer.primaryPhone), 75, phoneStartY);
    }

    let rightY = contentY;
    const job = selectedProject?.jobSiteAddress || {};
    let jobLine = '';
    if (job.street) jobLine += job.street + ', ';
    if (job.city) jobLine += job.city + ', ';
    if (job.state) jobLine += job.state + ' ';
    if (job.zip) jobLine += job.zip;
    if (job.country && job.country !== 'USA') jobLine += ', ' + job.country;
    else if (job.zip) jobLine += ', United States';
    
    if (jobLine.trim()) {
      const addressLines = doc.splitTextToSize(jobLine.trim(), 50);
      doc.text(addressLines, 140, rightY); // Project address stays at 140
      rightY += addressLines.length * 5;
    } else {
      doc.text('No project address on file', 140, rightY);
    }

    // 4. Table 
    let y = Math.max(leftY, phoneStartY + 5, rightY) + 10;
    // Columns are toggled in the form: Description + Amount always show; Qty and
    // Unit Price appear only when their checkbox is on.
    const tableColumn = ['Description'];
    if (showQtyCol) tableColumn.push('Qty');
    if (showUnitPriceCol) tableColumn.push('Unit Price');
    tableColumn.push('Amount');
    const tableRows = lineItems
      .filter((i) => i.description.trim())
      .map((item) => {
        const row = [item.description];
        if (showQtyCol) row.push(item.quantity);
        if (showUnitPriceCol) row.push(isLumpSum ? '-' : `$${formatPrice(item.unitPrice)}`);
        row.push(isLumpSum ? '-' : `$${(Number(item.quantity) * Number(item.unitPrice)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
        return row;
      });

    const quoteColStyles = {};
    let qc = 0;
    quoteColStyles[qc++] = { cellWidth: 'auto' };                 // Description
    if (showQtyCol) quoteColStyles[qc++] = { cellWidth: 16, halign: 'right' };
    if (showUnitPriceCol) quoteColStyles[qc++] = { cellWidth: 30, halign: 'right' };
    quoteColStyles[qc] = { cellWidth: 26, halign: 'right' };      // Amount

    doc.autoTable({
      startY: y,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [239, 70, 50], textColor: [255, 255, 255], fontStyle: 'bold' },
      styles: { fontSize: 11, cellPadding: 4, overflow: 'linebreak' },
      margin: { left: 15, right: 15 },
      columnStyles: quoteColStyles,
    });

    // 5. Totals & Memo
    const finalY = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    
    // Anchored perfectly to the right margin (x=195)
    doc.text(`Subtotal: $${formatPrice(subtotal)}`, 195, finalY, { align: 'right' });
    doc.text(`Total (${currency}): $${formatPrice(subtotal)}`, 195, finalY + 8, { align: 'right' });
    if (showCardFee) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(90, 90, 90);
      doc.text(`Card payments incur a 3.5% processing fee, added at checkout (approx. $${formatPrice(subtotal * 0.035)}).`, 195, finalY + 14, { align: 'right' });
      doc.setTextColor(0, 0, 0);
    }

    if (memo && memo.trim()) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text('Memo:', 15, finalY);
      const memoLines = doc.splitTextToSize(memo, 100);
      doc.text(memoLines, 15, finalY + 6);
    }

// 6. Footer
    // Changed variable name to force React to dump its cache!
    const absoluteBottomY = 285; 
    
    if (showSignature) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text('Signature: _________________________________________   Date: _______________', 15, absoluteBottomY - 12);
    }
    
    doc.setFontSize(10);
    doc.setTextColor(80);
    
    let textY = absoluteBottomY;
    
    if (showThankYou) {
      doc.text('We appreciate your business!', 15, textY);
      textY += 5; // Move down a line for the pay link if this text exists
    }
    
    if (showPayLink) {
      doc.text('Please pay at stalwart-electric.com/pay', 15, textY);
    }
    
    return doc;
  };
  // Download builds the same document and saves it.
  const generatePDF = () => {
    buildPDF().save(`Quote_${quoteNumber.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
  };


  return (
    <div className="bq-page" style={{ minHeight: '100vh', backgroundColor: '#f6f9fc', padding: '32px 40px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#32325d' }}>
      {/* Mobile-only layout (desktop untouched): stack columns, wrap actions, shrink the items grid */}
      <style>{`
        @media (max-width: 820px){
          .bq-page{ padding: 14px 12px !important; }
          .bq-head{ flex-wrap: wrap !important; gap: 12px !important; }
          .bq-head h1{ font-size: 22px !important; }
          .bq-actions{ flex-wrap: wrap !important; width: 100%; }
          .bq-actions button{ flex: 1 1 45%; padding: 10px 6px !important; font-size: 13px !important; }
          .bq-cols{ display: block !important; }
          .bq-form{ padding: 16px !important; }
          .bq-prev{ position: static !important; margin-top: 24px; padding: 12px !important; }
          .bq-irow{ grid-template-columns: 16px minmax(0,1fr) 52px 78px 22px !important; gap: 5px !important; }
          .bq-irow input{ font-size: 13px !important; padding: 8px 5px !important; }
          .bq-card input{ display: block; width: 100% !important; max-width: 100% !important; margin: 3px 0 !important; box-sizing: border-box; }
          .bq-card{ font-size: 14px; }
        }
      `}</style>
      {/* 🚀 THE SUCCESS TOAST */}
      {showSuccessToast && (
        <div style={{
          position: 'fixed',
          bottom: '40px',
          right: '40px',
          background: '#10b981', // Matches your green save button
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
          fontWeight: 600,
          fontSize: '16px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          animation: 'slideUp 0.3s ease-out'
        }}>
          <span style={{ fontSize: '20px' }}>✓</span> {toastMessage}
        </div>
      )}
      {bidParam && (
        <button onClick={() => navigate(`/contacts?bid=${bidParam}&section=quotes`)}
          style={{ background: 'none', border: 'none', padding: 0, marginBottom: '14px', color: '#6b7280', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
          ← Back to {selectedProject?.__bid && selectedProject?.title ? `"${selectedProject.title}"` : 'the bid'}
        </button>
      )}
      <div className="bq-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600 }}>Create Quote</h1>
        <div className="bq-actions" style={{ display: 'flex', gap: '12px' }}>
          <button style={{ padding: '10px 20px', border: '1px solid #e6e6e6', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: 600 }}
            onClick={() => navigate(bidParam ? `/contacts?bid=${bidParam}&section=quotes` : '/billing')}
            title={bidParam ? 'Back to the bid (Quotes tab)' : 'Back to Billing'}>
            Cancel
          </button>
          <button style={{ padding: '10px 20px', border: '1px solid #ef4632', color: '#ef4632', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: 600 }} onClick={handleClear}>
            Clear
          </button>
          <button onClick={handleSave} disabled={!selectedProject || sentLocked} title={sentLocked ? 'Sent — view only. Save as revision, or delete and recreate.' : (!selectedProject ? 'Select a project first' : '')} style={{ padding: '10px 20px', background: sentLocked ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: (!selectedProject || sentLocked) ? 'not-allowed' : 'pointer', opacity: (!selectedProject || sentLocked) ? 0.5 : 1 }}>
            Save
          </button>
          {currentQuoteId && savedNumber && (
            <button onClick={handleSaveRevision} title="Marks this quote Revised and creates a new one numbered with the next R-suffix — both PDFs stay unambiguous"
              style={{ padding: '10px 20px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}>
              Save as revision
            </button>
          )}
          <button onClick={generatePDF} disabled={!selectedProject} title={!selectedProject ? 'Select a project first' : ''} style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: !selectedProject ? 'not-allowed' : 'pointer', opacity: !selectedProject ? 0.5 : 1 }}>
            Download
          </button>
          <button onClick={handleSaveQuote} disabled={!selectedProject || !!sendStage} title={!selectedProject ? 'Select a project first' : ''}
            style={{ padding: '10px 20px', background: '#ef4632', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600,
              cursor: (!selectedProject || sendStage) ? 'not-allowed' : 'pointer', opacity: !selectedProject ? 0.5 : sendStage ? 0.85 : 1,
              animation: sendStage ? 'sendPulse 1.2s ease-in-out infinite' : 'none', minWidth: '80px' }}>
            {sendStage === 'saving' ? 'Saving…' : sendStage === 'pdf' ? 'Building PDF…' : sendStage === 'draft' ? 'Creating draft…' : 'Send'}
          </button>
          <style>{`@keyframes sendPulse { 0%,100% { opacity: 0.85; } 50% { opacity: 0.55; } }`}</style>
        </div>
      </div>

      <div className="bq-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
        {/* Left: Form */}
        <div className="bq-form" style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', padding: '32px' }}>
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Bid / Project</h2>
            <input
              type="text"
              placeholder={loadingProjects ? "Loading…" : "Find a bid or project by title, customer, or PO…"}
              value={projectSearch}
              onChange={(e) => setProjectSearch(e.target.value)}
              disabled={loadingProjects}
              style={{ width: '100%', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '16px', opacity: loadingProjects ? 0.6 : 1 }}
            />

            {fetchError && <div style={{ color: '#ef4632', marginTop: '8px' }}>{fetchError}</div>}

            {projectSearch && !loadingProjects && !quickProjOpen && (
              <div style={{ marginTop: '8px', border: '1px solid #ccd0d5', borderRadius: '6px', maxHeight: '280px', overflowY: 'auto', background: 'white' }}>
                {filteredBids.length > 0 && (
                  <>
                    <div style={{ padding: '6px 16px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', background: '#f8fafc' }}>Open bids</div>
                    {filteredBids.map((b) => (
                      <div key={b._id} onClick={() => { setSelectedProject(targetFromBid(b)); setProjectSearch(''); }}
                        style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}>
                        <strong style={{ color: '#4f46e5' }}>BID</strong> - {b.title} ({b.customer?.name || 'No customer'})
                      </div>
                    ))}
                  </>
                )}
                {filteredProjects.length > 0 && (
                  <>
                    <div style={{ padding: '6px 16px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.05em', background: '#f8fafc' }}>Projects (won work)</div>
                    {filteredProjects.map((p) => (
                      <div
                        key={p._id}
                        onClick={() => {
                          setSelectedProject(p);
                          setBillToCust(null); // new target → default bill-to
                          setProjectSearch('');
                        }}
                        style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                      >
                        <strong>{p.poNumber}</strong> - {p.title} ({p.customer?.name || 'No customer'})
                      </div>
                    ))}
                  </>
                )}
                {filteredBids.length === 0 && filteredProjects.length === 0 && (
                  <div style={{ padding: '12px 16px', color: '#888' }}>No matches found</div>
                )}
                <div onClick={openQuickProject} style={{ padding: '12px 16px', cursor: 'pointer', color: '#4f46e5', fontWeight: 600, borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, background: 'white' }}>
                  + New bid{projectSearch.trim() ? ` “${projectSearch.trim()}”` : ''}
                </div>
              </div>
            )}
            {!quickProjOpen && !loadingProjects && (
              <button type="button" onClick={openQuickProject} style={{ marginTop: '8px', background: 'none', border: 'none', color: '#4f46e5', fontWeight: 600, fontSize: '14px', cursor: 'pointer', padding: 0 }}>
                + New bid (and customer) — no PO or folders until it's won
              </button>
            )}

            {quickProjOpen && (
              <QuickCreateBid
                seedTitle={quickProjSeed}
                onDone={(b) => { setBids(prev => [b, ...prev]); setSelectedProject(targetFromBid(b)); setQuickProjOpen(false); }}
                onCancel={() => setQuickProjOpen(false)}
              />
            )}
          </section>

          {selectedProject && (
            <section className="bq-card" style={{ marginBottom: '40px', background: '#f0f8ff', padding: '16px', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>{selectedProject.__bid ? 'Selected Bid' : 'Selected Project'}</h3>
                {localStorage.getItem('role') === 'superadmin' && !cardForm && (
                  <button type="button" onClick={openCardEdit} title="Edit everything this document prints (super admin — this document only)"
                    style={{ background: 'none', border: '1px solid #dde3ea', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
                    ✎ Edit
                  </button>
                )}
              </div>
              {selectedProject.__bid
                ? <><strong>PO:</strong> <span style={{ color: '#6b7280' }}>none yet — assigned when the bid is won</span><br /></>
                : <><strong>PO:</strong> {cardForm ? <input value={cardForm.poNumber} onChange={e => setCardForm({ ...cardForm, poNumber: e.target.value })} style={{ ...ci, width: '90px' }} /> : renderCopy('po', selectedProject.poNumber)}<br /></>}
              <strong>Title:</strong> {cardForm ? <input value={cardForm.title} onChange={e => setCardForm({ ...cardForm, title: e.target.value })} style={{ ...ci, width: '320px' }} /> : renderCopy('title', selectedProject.title)}<br />
              <strong>Customer:</strong> {cardForm ? <><input value={cardForm.name} onChange={e => setCardForm({ ...cardForm, name: e.target.value })} style={{ ...ci, width: '180px' }} /><input value={cardForm.businessName} onChange={e => setCardForm({ ...cardForm, businessName: e.target.value })} placeholder="Business (optional)" style={{ ...ci, width: '180px' }} /></> : renderCopy('cust', selectedProject.customer?.name)}<br />
              <strong>Phone:</strong> {cardForm ? <input value={cardForm.phone} onChange={e => setCardForm({ ...cardForm, phone: fmtPhoneInput(e.target.value) })} style={{ ...ci, width: '140px' }} /> : renderCopy('phone', fmtPhone(selectedProject.customer?.primaryPhone))}<br />
              <strong>Email:</strong> {cardForm ? <input value={cardForm.email} onChange={e => setCardForm({ ...cardForm, email: e.target.value })} style={{ ...ci, width: '240px' }} /> : renderCopy('email', selectedProject.customer?.primaryEmail)}<br />
              <strong>Job site:</strong> {cardForm ? <>
                <input value={cardForm.street} onChange={e => setCardForm({ ...cardForm, street: e.target.value })} placeholder="Street" style={{ ...ci, width: '200px' }} />
                <input value={cardForm.city} onChange={e => setCardForm({ ...cardForm, city: e.target.value })} placeholder="City" style={{ ...ci, width: '110px' }} />
                <input value={cardForm.state} onChange={e => setCardForm({ ...cardForm, state: e.target.value })} placeholder="ST" style={{ ...ci, width: '42px' }} />
                <input value={cardForm.zip} onChange={e => setCardForm({ ...cardForm, zip: e.target.value })} placeholder="ZIP" style={{ ...ci, width: '64px' }} />
              </> : renderCopy('site', selectedProject.jobSiteAddress?.street ? `${selectedProject.jobSiteAddress.street}, ${selectedProject.jobSiteAddress.city || ''} ${selectedProject.jobSiteAddress.state || ''} ${selectedProject.jobSiteAddress.zip || ''}`.replace(/\s+/g, ' ').trim() : '')}<br />
              <strong>Quote #:</strong> {cardForm ? <input value={cardForm.number} onChange={e => setCardForm({ ...cardForm, number: e.target.value })} style={{ ...ci, width: '150px' }} /> : renderCopy('num', quoteNumber)}<br />
              {loadingQuotes && <span style={{ color: '#d97706' }}> (calculating next number...)</span>}
              {cardForm && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', maxWidth: '360px' }}>
                  <button type="button" onClick={() => setCardForm(null)} style={{ flex: 1, padding: '8px', background: '#eef0f3', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                  <button type="button" onClick={applyCardEdit} style={{ flex: 1, padding: '8px', background: '#d97706', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' }}>Apply to this document</button>
                </div>
              )}
            </section>
          )}
          {selectedProject && !selectedProject.__bid && <BillToPicker project={selectedProject} value={billToCust} onChange={setBillToCust} />}

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Currency</h2>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ width: '240px', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px' }}>
              <option value="USD">USD - United States Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
            </select>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, margin: '0 0 16px' }}>Items</h2>
            {/* Column header — Qty / Unit Price checkboxes toggle those columns on the PDF & live preview */}
            <div className="bq-irow" style={{ display: 'grid', gridTemplateColumns: '24px minmax(0, 3fr) 100px 140px 40px', gap: '12px', marginBottom: '10px', alignItems: 'center', fontSize: '13px', color: '#525f7f' }}>
              <span />
              <span>Description</span>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={showQtyCol} onChange={(e) => setShowQtyCol(e.target.checked)} style={{ width: '15px', height: '15px' }} /> Qty
              </label>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '5px', cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={showUnitPriceCol} onChange={(e) => setShowUnitPriceCol(e.target.checked)} style={{ width: '15px', height: '15px' }} /> Unit Price
              </label>
              <span />
            </div>
            {lineItems.map((item, index) => {
              const isDragging = drag && drag.id === item.id;
              // Show the drop-bar in the gap ABOVE this row (but never in the dragged row's own slot).
              const showGapBefore = drag && dropIndex === index
                && dropIndex !== drag.fromIndex && dropIndex !== drag.fromIndex + 1;
              return (
              <React.Fragment key={item.id}>
                {showGapBefore && <DropBar />}
              <div
                ref={(el) => (rowRefs.current[index] = el)}
                className="bq-irow"
                style={{
                  display: isDragging ? 'none' : 'grid', // the dragged row lifts out; a floating copy follows the cursor
                  gridTemplateColumns: '24px minmax(0, 3fr) 100px 140px 40px', // 24px drag handle; minmax(0,..) stops Description from squeezing Qty/Unit Price
                  gap: '12px',
                  marginBottom: '12px',
                  alignItems: 'center',
                }}
              >
                {/* 🚀 THE DRAG HANDLE */}
                <div
                  onPointerDown={(e) => startRowDrag(e, index)}
                  style={{ cursor: 'grab', color: '#cbd5e1', fontSize: '18px', display: 'flex', justifyContent: 'center', touchAction: 'none' }}
                >
                  ⠿
                </div>
                <input
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  onKeyDown={(e) => handleDescriptionKeyDown(e, index)}
                  ref={(el) => (descriptionRefs.current[index] = el)}
                  style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                />
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #ccd0d5',
                    borderRadius: '6px',
                    textAlign: 'right',
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                  }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={displayUnitPrice(item.id, item.unitPrice)}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                      setRawPriceInput(val);
                      const num = val === '' || val === '.' ? 0 : parseFloat(val);
                      updateItem(index, 'unitPrice', isNaN(num) ? 0 : num);
                    }
                  }}
                  onFocus={() => {
                    setFocusedPriceId(item.id);
                    setRawPriceInput(item.unitPrice.toString());
                  }}
                  onBlur={() => {
                    setFocusedPriceId(null);
                    setRawPriceInput('');
                  }}
                  placeholder="0.00"
                  style={{
                    padding: '12px 16px',
                    border: '1px solid #ccd0d5',
                    borderRadius: '6px',
                    textAlign: 'right',
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  onClick={() => removeItem(index)}
                  style={{ background: 'none', border: 'none', color: '#9b9fa7', fontSize: '20px', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              </React.Fragment>
              );
            })}
            {/* drop-at-the-very-end indicator */}
            {drag && dropIndex === lineItems.length
              && dropIndex !== drag.fromIndex && dropIndex !== drag.fromIndex + 1 && <DropBar />}
            <button onClick={addLineItem} style={{ color: '#635bff', background: 'none', border: '1px solid #635bff', borderRadius: '8px', padding: '8px 14px', fontSize: '15px', cursor: 'pointer' }}>
              + Add line item
            </button>
            {projectInformalEstimates.length > 0 && (
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  onClick={() => setShowIePicker(v => !v)}
                  style={{ color: '#635bff', background: 'none', border: '1px solid #635bff', borderRadius: '8px', padding: '8px 14px', fontSize: '15px', cursor: 'pointer', marginLeft: '16px' }}
                >
                  + Add item from Informal Estimates
                </button>
                {showIePicker && (
                  <>
                    <span onClick={() => setShowIePicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                    <div style={{ position: 'absolute', bottom: '28px', left: '16px', zIndex: 50, background: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', minWidth: '320px', maxHeight: '260px', overflowY: 'auto' }}>
                      {projectInformalEstimates.map(ie => {
                        // Not clickable if already on the document OR already fully
                        // paid (paid work has nothing left to bill).
                        const disabled = addedIeIds.includes(ie._id) || ie.fullyPaid;
                        const tag = addedIeIds.includes(ie._id) ? ' (added)' : ie.fullyPaid ? ' (fully paid)' : '';
                        return (
                        <div
                          key={ie._id}
                          onClick={() => { if (!disabled) addItemFromInformalEstimate(ie); }}
                          style={{ padding: '10px 14px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.75 : 1, borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', gap: '12px', color: disabled ? '#6b7280' : '#32325d' }}
                          onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = '#f6f9fc'; }}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          <span style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ie.description}{tag}
                          </span>
                          <strong style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>${Number(ie.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                        </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </span>
            )}
            {/* Floating copy of the row that follows the cursor (no native ghost = no snap-back) */}
            {drag && pointer && (() => {
              const di = lineItems.find((i) => i.id === drag.id) || {};
              return (
                <div style={{
                  position: 'fixed', left: pointer.x - drag.dx, top: pointer.y - drag.dy,
                  width: drag.w, height: drag.h, pointerEvents: 'none', zIndex: 1000,
                  display: 'grid', gridTemplateColumns: '24px minmax(0, 3fr) 100px 140px 40px',
                  gap: '12px', alignItems: 'center', background: 'white',
                  boxShadow: '0 10px 28px rgba(0,0,0,0.20)', borderRadius: '8px', opacity: 0.97,
                }}>
                  <div style={{ color: '#a3aab8', fontSize: '18px', display: 'flex', justifyContent: 'center' }}>⠿</div>
                  <div style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{di.description || 'Item'}</div>
                  <div style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', textAlign: 'right' }}>{di.quantity}</div>
                  <div style={{ padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px', textAlign: 'right' }}>${formatPrice(di.unitPrice)}</div>
                  <div />
                </div>
              );
            })()}
          </section>

          <section style={{ marginBottom: '40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showSignature}
                onChange={(e) => setShowSignature(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show signature
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showPayLink}
                onChange={(e) => setShowPayLink(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show pay link
            </label>
            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showCardFee}
                onChange={(e) => setShowCardFee(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show card-payment total (+3.5% processing fee)
            </label>

            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showValidFor}
                onChange={(e) => setShowValidFor(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show Valid For
            </label>

            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isLumpSum}
                onChange={(e) => setIsLumpSum(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Hide individual amounts (Lump Sum Quote)
            </label>

            <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showThankYou}
                onChange={(e) => setShowThankYou(e.target.checked)}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Show Thank You message
            </label>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Quote Valid For</h2>
            <input
              type="number"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              min="1"
              style={{ width: '120px', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px' }}
            /> days
          </section>

          <section>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Memo</h2>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="Optional notes..."
              style={{ width: '100%', height: '100px', padding: '12px 16px', border: '1px solid #ccd0d5', borderRadius: '6px' }}
            />
          </section>
        </div>

        {/* Right: Preview — real jsPDF rendered to an iframe (WYSIWYG), always on */}
        <div className="bq-prev" style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', padding: '20px', position: 'sticky', top: '100px' }}>
          {/* Hidden preload so jsPDF always has the logo cached for the live render */}
          <img src="/media/logo_round.png" alt="" style={{ display: 'none' }} />
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Live Preview</h2>
          {livePdfUrl ? (
            <iframe
              src={`${livePdfUrl}#toolbar=0&navpanes=0&view=FitH`}
              title="Live quote PDF"
              style={{ width: '100%', aspectRatio: '8.5 / 11', border: '1px solid #ddd', borderRadius: '8px' }}
            />
          ) : (
            <div style={{ width: '100%', aspectRatio: '8.5 / 11', border: '1px dashed #ccd0d5', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9b9fa7', fontSize: '15px', textAlign: 'center', padding: '24px' }}>
              Generating preview…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateQuote;